import Konva from 'konva';
import { konvaSettings } from '../../konvaSettings';

export const HANGER_DISTANCE = 0.08;

const HANGER_PREVIEW_COUNT = 3;
const HANGER_PREVIEW_SIDE_PAD = 0.05;
const HANGER_PREVIEW_PRODUCT_WIDTH = 0.07;
const HANGER_PREVIEW_PRODUCT_HEIGHT = 0.2;
const HANGER_PREVIEW_PRODUCT_DEPTH = 0.045;
const HANGER_PREVIEW_PRODUCT_GAP = 0.022;
const HANGER_PREVIEW_SHOULDER = 0.02;
const HANGER_PREVIEW_UNIT = 100;
const THREE_QUARTER_YAW = (-50 * Math.PI) / 180;
const THREE_QUARTER_PITCH = (12 * Math.PI) / 180;

export interface HangerPreviewLayout {
	diameter: number;
	radius: number;
	showHangers: boolean;
	hangerHeight: number;
	fill: string;
	barWidth: number;
	productWidth: number;
	productHeight: number;
	productDepth: number;
	productXs: Array<number>;
}

interface ProjectedPoint {
	x: number;
	y: number;
	z: number;
}

interface ProjectedFace {
	corners: Array<ProjectedPoint>;
	fill: string;
	depth: number;
}

export function getHangerPreviewLayout(diameter: number, showHangers: boolean, fill: string): HangerPreviewLayout {
	const productXs = Array.from(
		{ length: HANGER_PREVIEW_COUNT },
		(_, index) => HANGER_PREVIEW_SIDE_PAD + index * (HANGER_PREVIEW_PRODUCT_WIDTH + HANGER_PREVIEW_PRODUCT_GAP)
	);

	return {
		diameter,
		radius: diameter / 2,
		showHangers,
		hangerHeight: showHangers ? HANGER_DISTANCE : 0,
		fill,
		barWidth:
			HANGER_PREVIEW_SIDE_PAD * 2 +
			HANGER_PREVIEW_COUNT * HANGER_PREVIEW_PRODUCT_WIDTH +
			(HANGER_PREVIEW_COUNT - 1) * HANGER_PREVIEW_PRODUCT_GAP,
		productWidth: HANGER_PREVIEW_PRODUCT_WIDTH,
		productHeight: HANGER_PREVIEW_PRODUCT_HEIGHT,
		productDepth: HANGER_PREVIEW_PRODUCT_DEPTH,
		productXs
	};
}

export function drawHangerPreview2D(layout: HangerPreviewLayout) {
	const unit = HANGER_PREVIEW_UNIT;
	const barWidth = layout.barWidth * unit;
	const barHeight = layout.diameter * unit;
	const productWidth = layout.productWidth * unit;
	const productHeight = layout.productHeight * unit;
	const hangerHeight = layout.hangerHeight * unit;
	const productY = barHeight + hangerHeight;
	const group = new Konva.Group();

	group.add(
		new Konva.Rect({
			x: 0,
			y: 0,
			width: barWidth,
			height: barHeight,
			cornerRadius: barHeight / 2,
			fill: layout.fill,
			stroke: konvaSettings.strokeColor,
			strokeWidth: 1
		})
	);

	layout.productXs.forEach((productX) => {
		group.add(
			new Konva.Rect({
				x: productX * unit,
				y: productY,
				width: productWidth,
				height: productHeight,
				fill: konvaSettings.fillGreyColor,
				stroke: konvaSettings.strokeColor,
				strokeWidth: 1
			})
		);
	});

	if (layout.showHangers) {
		layout.productXs.forEach((productX) => {
			addHangerPreview2D(group, productX * unit, barHeight, productWidth, hangerHeight, barHeight / 2);
		});
	}

	return group;
}

export function drawHangerPreview3D(layout: HangerPreviewLayout) {
	const productTop = -(layout.radius + layout.hangerHeight);
	const group = new Konva.Group();
	const productFaces: Array<ProjectedFace> = [];
	const barFaces: Array<ProjectedFace> = [];

	collectBoxFaces(
		barFaces,
		0,
		-layout.radius,
		-layout.radius,
		layout.barWidth,
		layout.diameter,
		layout.diameter,
		layout.fill
	);

	layout.productXs.forEach((productX) => {
		collectBoxFaces(
			productFaces,
			productX,
			productTop - layout.productHeight,
			-layout.productDepth / 2,
			layout.productWidth,
			layout.productHeight,
			layout.productDepth,
			konvaSettings.fillGreyColor
		);

		if (layout.showHangers) {
			collectBoxFaces(
				productFaces,
				productX + layout.productWidth / 4,
				productTop,
				-0.006,
				layout.productWidth / 2,
				HANGER_PREVIEW_SHOULDER,
				0.012,
				konvaSettings.fillDarkColor
			);
		}
	});

	paintProjectedFaces(group, productFaces);
	paintProjectedFaces(group, barFaces);

	if (layout.showHangers) {
		layout.productXs.forEach((productX) => {
			addHangerPreview3D(group, productX + layout.productWidth / 2, layout.radius, productTop);
		});
	}

	return group;
}

function addHangerPreview2D(
	parent: Konva.Group,
	x: number,
	barBottom: number,
	productWidth: number,
	hangerHeight: number,
	arcRadius: number
) {
	const hanger = new Konva.Group({
		x,
		y: barBottom,
		width: productWidth,
		height: hangerHeight
	});
	const hook: Array<number> = [];
	const cx = productWidth / 2;
	const cy = -arcRadius;

	for (let index = 0; index <= 12; index++) {
		const t = (index / 12) * Math.PI;

		hook.push(cx + arcRadius * Math.sin(t), cy - arcRadius * Math.cos(t));
	}

	hanger.add(
		new Konva.Line({
			points: hook,
			stroke: konvaSettings.fillDarkColor,
			strokeWidth: 2,
			lineCap: 'round',
			strokeScaleEnabled: false
		})
	);
	hanger.add(
		new Konva.Line({
			points: [productWidth / 2, 0, productWidth / 2, hangerHeight],
			stroke: konvaSettings.fillDarkColor,
			strokeWidth: 2,
			lineCap: 'round',
			strokeScaleEnabled: false
		})
	);
	hanger.add(
		new Konva.Rect({
			x: productWidth / 4,
			y: hangerHeight - HANGER_PREVIEW_SHOULDER * HANGER_PREVIEW_UNIT,
			width: productWidth / 2,
			height: HANGER_PREVIEW_SHOULDER * HANGER_PREVIEW_UNIT,
			fill: konvaSettings.fillDarkColor
		})
	);
	parent.add(hanger);
}

function addHangerPreview3D(group: Konva.Group, cx: number, radius: number, productTop: number) {
	const hook: Array<number> = [];

	for (let index = 0; index <= 12; index++) {
		const t = (index / 12) * Math.PI;
		const point = projectThreeQuarter(cx, radius * Math.cos(t), radius * Math.sin(t));

		hook.push(point.x, point.y);
	}

	group.add(
		new Konva.Line({
			points: hook,
			stroke: konvaSettings.fillDarkColor,
			strokeWidth: 2,
			lineCap: 'round',
			strokeScaleEnabled: false
		})
	);

	const stemTop = projectThreeQuarter(cx, -radius, 0);
	const stemBottom = projectThreeQuarter(cx, productTop + HANGER_PREVIEW_SHOULDER, 0);

	group.add(
		new Konva.Line({
			points: [stemTop.x, stemTop.y, stemBottom.x, stemBottom.y],
			stroke: konvaSettings.fillDarkColor,
			strokeWidth: 2,
			lineCap: 'round',
			strokeScaleEnabled: false
		})
	);
}

function rotateThreeQuarter(x: number, y: number, z: number): ProjectedPoint {
	const cosYaw = Math.cos(THREE_QUARTER_YAW);
	const sinYaw = Math.sin(THREE_QUARTER_YAW);
	const xYaw = x * cosYaw + z * sinYaw;
	const zYaw = -x * sinYaw + z * cosYaw;
	const cosPitch = Math.cos(THREE_QUARTER_PITCH);
	const sinPitch = Math.sin(THREE_QUARTER_PITCH);

	return {
		x: xYaw,
		y: y * cosPitch - zYaw * sinPitch,
		z: y * sinPitch + zYaw * cosPitch
	};
}

function projectThreeQuarter(x: number, y: number, z: number): ProjectedPoint {
	const rotated = rotateThreeQuarter(x, y, z);

	return {
		x: rotated.x * HANGER_PREVIEW_UNIT,
		y: -rotated.y * HANGER_PREVIEW_UNIT,
		z: rotated.z
	};
}

function flattenPoints(...corners: Array<ProjectedPoint>): Array<number> {
	return corners.flatMap((corner) => [corner.x, corner.y]);
}

function faceDepth(corners: Array<ProjectedPoint>) {
	return corners.reduce((sum, corner) => sum + corner.z, 0) / corners.length;
}

function insetCorners(corners: Array<ProjectedPoint>, pixels: number) {
	const centerX = corners.reduce((sum, corner) => sum + corner.x, 0) / corners.length;
	const centerY = corners.reduce((sum, corner) => sum + corner.y, 0) / corners.length;

	return corners.map((corner) => {
		const dx = centerX - corner.x;
		const dy = centerY - corner.y;
		const length = Math.hypot(dx, dy) || 1;

		return {
			...corner,
			x: corner.x + (dx / length) * pixels,
			y: corner.y + (dy / length) * pixels
		};
	});
}

function darkenFill(color: string, amount: number) {
	if (!/^#([0-9a-f]{6})$/i.test(color)) {
		return color;
	}

	const value = parseInt(color.slice(1), 16);
	const factor = 1 - amount;
	const toHex = (channel: number) =>
		Math.max(0, Math.min(255, Math.round(channel * factor)))
			.toString(16)
			.padStart(2, '0');

	return `#${toHex((value >> 16) & 255)}${toHex((value >> 8) & 255)}${toHex(value & 255)}`;
}

function collectBoxFaces(
	faces: Array<ProjectedFace>,
	x: number,
	y: number,
	z: number,
	width: number,
	height: number,
	depth: number,
	fill: string
) {
	const leftBottomFront = projectThreeQuarter(x, y, z + depth);
	const rightBottomFront = projectThreeQuarter(x + width, y, z + depth);
	const rightTopFront = projectThreeQuarter(x + width, y + height, z + depth);
	const leftTopFront = projectThreeQuarter(x, y + height, z + depth);
	const leftBottomBack = projectThreeQuarter(x, y, z);
	const rightBottomBack = projectThreeQuarter(x + width, y, z);
	const rightTopBack = projectThreeQuarter(x + width, y + height, z);
	const leftTopBack = projectThreeQuarter(x, y + height, z);
	const sideFill = darkenFill(fill, 0.12);
	const topFill = darkenFill(fill, 0.05);

	pushProjectedFace(faces, [leftBottomFront, rightBottomFront, rightTopFront, leftTopFront], fill);
	pushProjectedFace(faces, [rightBottomFront, rightBottomBack, rightTopBack, rightTopFront], sideFill);
	pushProjectedFace(faces, [leftBottomBack, leftBottomFront, leftTopFront, leftTopBack], sideFill);
	pushProjectedFace(faces, [leftTopFront, rightTopFront, rightTopBack, leftTopBack], topFill);
	pushProjectedFace(faces, [leftBottomBack, rightBottomBack, rightBottomFront, leftBottomFront], sideFill);
	pushProjectedFace(faces, [rightBottomBack, leftBottomBack, leftTopBack, rightTopBack], sideFill);
}

function pushProjectedFace(faces: Array<ProjectedFace>, corners: Array<ProjectedPoint>, fill: string) {
	const ax = corners[1].x - corners[0].x;
	const ay = corners[1].y - corners[0].y;
	const bx = corners[2].x - corners[0].x;
	const by = corners[2].y - corners[0].y;

	if (ax * by - ay * bx >= 0) {
		return;
	}

	faces.push({
		corners,
		fill,
		depth: faceDepth(corners)
	});
}

function paintProjectedFaces(group: Konva.Group, faces: Array<ProjectedFace>) {
	[...faces]
		.sort((left, right) => left.depth - right.depth)
		.forEach((face) => {
			group.add(
				new Konva.Line({
					points: flattenPoints(...insetCorners(face.corners, 0.2)),
					closed: true,
					fill: face.fill,
					strokeEnabled: false,
					perfectDrawEnabled: false
				})
			);
			group.add(
				new Konva.Line({
					points: flattenPoints(...face.corners),
					closed: true,
					stroke: konvaSettings.strokeColor,
					strokeWidth: 1,
					lineJoin: 'round',
					lineCap: 'round',
					perfectDrawEnabled: false,
					fillEnabled: false
				})
			);
		});
}
